<template>
  <div
    class="Chat"
    :class="{
      'Slim': slim,
      'Chat--hasThirdPartMessageSwitch': hasThirdPartMessageSwitch,
      'Chat--canShowPredefinedMessages': canShowPredefinedMessages,
      'BiggerText': useUIStore().accessibility.biggerText === 1,
      'BiggerText2': useUIStore().accessibility.biggerText === 2,
      'BiggerText3': useUIStore().accessibility.biggerText === 3,
    }"
    :style="!isSafari ? `max-height: ${height - 115}px` : false"
  >
    <div
      ref="feed"
      class="ChatDivWrapper"
    >
      <div
        v-if="!isScrolledToBottom"
        class="flex w-full justify-center ScrollToBottomBtnWrapper"
      >
        <el-button
          type="primary"
          circle
          class="hover:shadow-base ScrollToBottomBtn"
          @click="scrollToBottom"
        >
          <icon-caret-down-rounded />
        </el-button>
      </div>
      <div class="ChatDiv">
        <div
          v-if="!messageBlocks.length"
          class="color-muted text-center text-sm"
        >
          {{ $t('ChatFeed.emptyState') }}
        </div>
        <div
          v-for="(block, index) in messageBlocks"
          :key="block.id"
          class="MessageBlock"
          :class="[block.direction]"
        >
          <div class="MessageBlockContent">
            <div
              v-if="block.direction === 'in' || !slim"
              class="MessageBlockSidePanel"
            >
              <el-tooltip
                :content="block.third_party ? $t('ChatFeed.thirdParty') : block.user.name"
              >
                <UserAvatar
                  v-if="block.direction === 'in' && !block.third_party"
                  :user="block.user"
                />
                <div
                  v-else
                  class="UserAvatar"
                  :class="{
                    'UserAvatar--ThirdParty': block.third_party,
                  }"
                >
                  <icon-phone
                    v-if="block.third_party"
                    class="color-muted"
                  />
                </div>
              </el-tooltip>
            </div>
            <div class="Messages">
              <el-tooltip
                v-for="(message) in block.messages"
                :key="message.id"
                :content="getTooltipContent(block, message)"
                :placement="block.direction === 'in' ? `left` : 'right'"
                transition="none"
                :disabled="message.edit"
              >
                <ChatMessage
                  :message="message"
                  :direction="block.direction"
                  :can-edit="canEdit && !block.is_typing"
                  :show-is-typing-icon="block.is_typing && showIsTypingIcon && block.direction === 'out'"
                  :last-read-by="lastReadByList ? lastReadByList[message.id] : []"
                  @toggle-edit-message="toggleEditMessage(message)"
                  @resend-message="resendMessage(message)"
                  @edit-message="editMessage"
                />
              </el-tooltip>
            </div>
          </div>
          <div class="MessageBlockFooter">
            <div
              v-if="(index == messageBlocks.length - (messagesIsTyping.length !== 0 ? 2 : 1)) || block.is_typing"
              :key="index"
              class="BlockLast text-sm"
            >
              <span class="font-semibold">
                <template v-if="block.direction === 'in'">
                  <template v-if="block.third_party">
                    {{ $t('ChatFeed.thirdParty') }}
                  </template>
                  <template v-else>
                    {{ block.user.name ? block.user.name : `${block.user.first_name} ${block.user.last_name}` }}
                  </template>
                </template>
              </span>
              <span
                v-if="block.is_typing && block.direction === 'in'"
                class="text-muted ml-1"
              >
                {{ $t('ChatFeed.isTyping') }}
              </span>
              <span
                v-if="!block.is_typing"
                class="Timestamp text-muted"
              >
                {{ block.tooltip_time }}
              </span>
            </div>
          </div>
        </div>

        <!-- <template v-if="requestState === 3">
          <div class="flex items-center flex-col mt-8">
            <div class="like-h4 text-text-light mb-3">
              {{ request.service_id === 1 ? 'Tlumočník' : 'Přepisovatel' }} pozastavil požadavek.
            </div>
            <div class="text-sm text-text-light">
              Vyčkejte než se opět připojí.
            </div>
          </div>
        </template> -->
        <template v-if="requestState === 4 || requestState === 5">
          <div class="flex flex-col items-center mt-8 mb-6 px-4 gap-2">
            <div class="like-h4 text-text-light">
              {{ $t('ChatFeed.requestWasClosed') }}
            </div>
            <div class="text-sm text-muted">
              {{ finalizedAt ? dayjs(finalizedAt).format('DD. MM. YYYY, HH:mm') : '' }}
            </div>

            <template v-if="showClosedCard">
              <strong
                v-if="isHistorySent"
                class="text-sm text-primary-500 text-center"
              >
                {{ $t('ChatFeed.chatHistoryWasSentTo') }} {{ useAuthStore().user?.email }}
              </strong>
              <button
                v-else
                type="button"
                class="ClosedCard__link"
                :disabled="isHistorySending"
                @click="sendHistoryToEmail"
              >
                {{ $t('ChatFeed.sentChatHistory') }}
              </button>

              <div class="flex flex-col items-center gap-3 mt-6">
                <div class="flex items-center justify-center gap-2 flex-wrap">
                  <p class="text-sm text-text-light text-center m-0">
                    {{ $t('ChatFeed.rateDescription') }}
                  </p>
                  <VideoHelp
                    :youtube="$t('ChatFeed.rateVideo')"
                    :title="$t('ChatFeed.rateVideoTitle')"
                  />
                </div>
                <el-button
                  type="primary"
                  size="small"
                  tag="a"
                  :href="$t('ChatFeed.rateUrl')"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="no-underline hover:no-underline"
                >
                  {{ $t('ChatFeed.rateSubmit') }}
                </el-button>
              </div>
            </template>
          </div>
        </template>
        <div
          v-if="canShowPredefinedMessages && isPredefinedMessagesShown"
          class="absolute bottom-3.5 left-0 pl-2 pr-6 flex flex-col items-start gap-1.5 w-full"
        >
          <button
            v-for="(predefinedMessage, index) in predefinedMessages"
            :key="index"
            class="block max-w-full text-xs whitespace-nowrap bg-light truncate rounded-lg py-1 px-1.5 transition hover:bg-border"
            :title="predefinedMessage"
            @click="messageToSend = predefinedMessage; $refs.input.focus()"
          >
            {{ predefinedMessage }}
          </button>
        </div>
      </div>
    </div>
    <div
      v-if="!request || (request?.state_id === 1 || request?.state_id === 2)"
      class="InputDiv"
    >
      <div
        v-if="canShowPredefinedMessages"
        class="absolute left-1 -top-8"
      >
        <button
          class="bg-white rounded-full p-1"
          @click="isPredefinedMessagesShown = !isPredefinedMessagesShown"
        >
          <icon-dots-horizontal class="text-text-light w-5 h-auto" />
        </button>
      </div>
      <div
        v-if="hasThirdPartMessageSwitch"
        class="ThirdPartSwitch"
      >
        <el-switch
          v-model="isMessageByThirdParty"
          :width="36"
          :active-text="$t('ChatFeed.thirdPartySpeaking')"
          :inactive-text="$t('ChatFeed.transcriberSpeaking')"
        />
      </div>

      <textarea
        ref="input"
        v-model="messageToSend"
        :placeholder="$t('ChatFeed.messagePlaceholder')"
        data-cy="chat-input"
        @input="submitIsTypingMessage"
        @keyup.enter.exact="submitMessage"
        @keyup.ctrl.enter.exact="messageToSend = messageToSend + '\n'"
      />
      <div class="SubmitMessageButtonWrapper flex">
        <el-button
          type="primary"
          circle
          data-cy="chat-submit"
          :class="isMessageByThirdParty ? 'bg-info' : ''"
          :disabled="disabledSubmit"
          @click="submitMessage"
        >
          <icon-right />
        </el-button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    request: {
      type: Object,
      default: () => {},
    },
    messages: {
      type: Array,
      default: () => [],
    },
    submitDisabled: {
      type: Boolean,
      default: false,
    },
    slim: {
      type: Boolean,
      default: false,
    },
    canEdit: {
      type: Boolean,
      default: false,
    },
    isVisible: {
      type: Boolean,
      default: true,
    },
  },
  emits: ['submit-message'],
  setup() {
    const { height } = useWindowSize();

    return {
      height,
    };
  },
  data() {
    return {
      isMessageByThirdParty: false,
      messageToSend: '',
      sendingMessage: false,
      isScrolledToBottom: false,
      isSafari: navigator ? /^((?!chrome|android).)*safari/i.test(navigator.userAgent) : false,
      isHistorySending: false,
      isHistorySent: false,
      isPredefinedMessagesShown: false,
      shouldClearIsTypingMessage: false,
      lastNotifiedMessageId: null,
      audio: {
        newMessage: new Audio('/sounds/chat_message.mp3'),
      },
    };
  },
  computed: {
    me() {
      return useAuthStore().user;
    },
    disabledSubmit() {
      return !this.messageToSend || this.submitDisabled;
    },
    finalizedAt() {
      return this.request ? this.request.finalized_at : false;
    },
    showClosedCard() {
      const user = useAuthStore().user;
      return !user?.is_operator && !user?.is_temp;
    },
    showIsTypingIcon() {
      return this.slim || (this.request && !this.request.state_id != 1);
    },
    messagesIsTyping() {
      return this.messages.filter((message) => message.is_typing && message.content);
    },
    hasThirdPartMessageSwitch() {
      return this.request && this.request.service_id === 2 && useAuthStore().user?.is_operator;
    },
    messageBlocks() {
      const blocks = [];

      let block = {
        direction: '',
        messages: [],
      };

      let messages = this.messages.map((m) => ({ ...m })).filter((message) => !message.is_typing);
      messages = [...messages, ...this.messagesIsTyping].filter((message) => !!message.content);

      for (let index = 0; index < messages.length; index++) {
        const message = messages[index];
        const next = messages[index + 1];

        const momentCreatedAt = dayjs(message.created_at);
        message.tooltip_time = momentCreatedAt.isValid()
          ? momentCreatedAt.calendar(null, {
            sameDay: 'HH:mm',
            lastDay: '[včera] HH:mm',
            lastWeek: 'dddd HH:mm',
            sameElse: 'DD.MM.YYYY HH:mm',
          })
          : '';

        block.messages.push(message);
        block.is_typing = block.messages.some((message) => message.is_typing);
        block.third_party = block.messages.some((message) => message.third_party);

        // close the current message block
        if (!next || // if this is the last message
          next.sender.id != message.sender.id || // if the next message is from another user
          next.third_party !== message.third_party || // if the next message is from third party
          dayjs(next.created_at).diff(message.created_at, 'minute') > 5 || // if the delay between the messages is more than 5 mins
          next.is_typing
        ) {
          block.direction = message.sender.id == this.me.id ? 'out' : 'in';
          const name = message.sender.name ? message.sender.name : `${message.sender.first_name} ${message.sender.last_name}`;
          block.user = { ...message.sender, name };
          block.id = message.id;
          block.tooltip_time = message.tooltip_time;
          blocks.push(block);

          block = {
            messages: [],
          };
        }
      }

      return blocks;
    },
    requestState() {
      return this.request ? this.request.state_id : null;
    },
    isMessageByThirdPartySessionKey() {
      return this.request && this.request.id && `isMessageByThirdParty-${this.request.id}`;
    },
    predefinedMessages() {
      if (this.request.service_id === 1) {
        return [
          'Dobrý den, můžeme začít?',
          'Bohužel jste se nepřipojil/a, váš požadavek musíme zrušit. Pokud potřebujete tlumočit, udělejte si prosím nový požadavek.',
          'Pokud vám Tichá linka nefunguje, podívejte se na videa zde: https://www.tichalinka.cz/technicka-pomoc',
          'Souhlasíte s podmínkami poskytování tlumočnických služeb a chcete se stát klientem Tlumočnických služeb Tichého světa?',
        ];
      }

      if (this.request.service_id === 2) {
        return [
          'Pokud chcete, můžete se mnou po kliknutí na zelené tlačítko “Přijmout” mluvit. Pokud chcete psát, využijte chat.',
          'Chcete PŘEPIS TELEFONNÍHO HOVORU, budeme komunikovat pouze přes tento chat. Já budu psát, co říká volaný, a přečtu mu, co píšete vy. Můžeme volat?',
          'Jsem přepisovatelka pro osoby se sluchovým postižením. Chcete OSOBNÍ HOVOR. Nyní se tedy spojíme přes audio a já vám přepíšu, co uslyším. Prosím klikněte na zelené tlačítko “Přijmout”. Potom můžete začít mluvit, hovor budu přepisovat zde na obrazovce.',
          'Chcete ČÁSTEČNÝ PŘEPIS TELEFONNÍHO HOVORU. Nyní se tedy spolu spojíme přes audio, abyste před zavoláním mohla mluvit se mnou a zkontrolovali jsme mikrofon. Prosím klikněte na zelené tlačítko “Přijmout”.',
          'Slyším vás. Teď vytočím telefonní číslo, vysvětlím volanému službu a potom vás pozvu k hovoru. Moment, prosím.',
          'Prosím klikněte na zelené tlačítko „Přijmout", potom můžete mluvit s volaným a já pro vás budu přepisovat tady v chatu, co říká.',
          'Bude to vše? Znovu se s vámi spojím, ať nemusíte psát. Prosím klikněte na zelené tlačítko “Příjmout”.',
          'Souhlasíte s podmínkami poskytování tlumočnických služeb a chcete se stát klientem Tlumočnických služeb Tichého světa?',
        ];
      }

      return [];
    },
    canShowPredefinedMessages() {
      return !this.messageToSend && useAuthStore().user?.is_operator && (this.requestState === 1 || this.requestState === 2) && this.request?.client?.lang !== 'ua';
    },
    lastReadByList() {
      const allReadByUsersIds = _uniq(this.messages.map((message) => message.read_by?.map((user) => user.id)).flat());
      const usersWithLastReadMessageId = allReadByUsersIds?.map((userId) => ({
        userId,
        messageId: this.messages.findLast((message) =>
          message.read_by?.some((user) => user.id === userId && user.id !== this.me.id)
          && message.sender.id === this.me.id)
          ?.id,
      }));

      const list = {};

      this.messages.forEach((message) => {
        list[message.id] = message.read_by?.filter((user) => usersWithLastReadMessageId?.some((userWithLastReadMessageId) => userWithLastReadMessageId.userId === user.id && userWithLastReadMessageId.messageId === message.id));
      });

      return list;
    },
  },
  watch: {
    messageBlocks: {
      async handler(newBlocks, oldBlocks) {
        if (this.isScrolledToBottom || !oldBlocks.length) {
          this.$nextTick(() => {
            this.scrollToBottom();
          });
        }

        const lastBlock = newBlocks.slice(-1)[0];
        if (lastBlock && lastBlock.direction !== 'out' && !lastBlock.is_typing) {
          // is incoming message
          const lastWsMessage = lastBlock.messages.findLast((message) => message.isFromWebSocket && !message.is_typing);

          // notify only when a message with a new id arrives — the watcher also fires on
          // unrelated mutations of the same messages (read_by updates, edits, typing),
          // which used to replay the sound for a message already notified (#1561)
          if (lastWsMessage && lastWsMessage.id !== this.lastNotifiedMessageId) {
            this.lastNotifiedMessageId = lastWsMessage.id;

            const windowHidden = document.hidden;

            if ((windowHidden || !this.isVisible) && useAuthStore().isLoggedIn) {
              const isPlayedByExtension = await useUIStore().chromeExtensionSendMessage({ playAudio: 'newMessage', message: JSON.stringify(lastWsMessage) });
              if (!isPlayedByExtension) {
                this.audio.newMessage.currentTime = 0;
                this.audio.newMessage.play().catch((error) => {
                  console.warn(error);
                });
              }
            }
          }
        }
      },
      deep: true,
    },
    isVisible: {
      handler() {
        this.scrollToBottom();
      },
      immediate: true,
    },
    'request.id'() {
      this.updateIsMessageByThirdParty();
    },
    isMessageByThirdParty(isMessageByThirdParty) {
      if (isMessageByThirdParty) {
        window.sessionStorage.setItem(this.isMessageByThirdPartySessionKey, true);
      } else {
        window.sessionStorage.removeItem(this.isMessageByThirdPartySessionKey);
      }
    },
  },
  created() {
    this.scrollToBottom();
  },
  mounted() {
    this.scrollToBottom();
    this.$refs.feed.addEventListener('scroll', this.onScroll);

    this.updateIsMessageByThirdParty();
  },
  beforeUnmount() {
    // if there are any messages, take any of them and use it to clear possible edit state
    if (this.messages.length) {
      this.$store.dispatch('chat/clearEditMessage', this.messages[0]);
      this.$refs.feed.removeEventListener('scroll', this.onScroll);
    }
  },
  methods: {
    scrollToBottom() {
      setTimeout(() => {
        if (this.$refs.feed) {
          this.$refs.feed.scrollTop = this.$refs.feed.scrollHeight - this.$refs.feed.clientHeight;
          this.isScrolledToBottom = true;
        }
      }, 50);
    },
    submitMessage() {
      if (!this.disabledSubmit) {
        this.$emit('submit-message', this.messageToSend.trim(), { isTyping: false, isMessageByThirdParty: this.isMessageByThirdParty });
        this.messageToSend = '';

        if (this.shouldClearIsTypingMessage) {
          this.$emit('submit-message', '', { isTyping: true, isMessageByThirdParty: this.isMessageByThirdParty }); // Clear is_typing message
        }
      }
    },
    submitIsTypingMessage: _debounce(function() {
      if (!this.submitDisabled && this.request && this.request.service_id == 2 && (this.request.state_id == 2 || this.request.state_id == 3)) {
        this.$emit('submit-message', this.messageToSend.trim(), { isTyping: true, isMessageByThirdParty: this.isMessageByThirdParty });
        this.shouldClearIsTypingMessage = true;
      }
    }, 50),
    resendMessage(message) {
      this.$store.commit('chat/MESSAGE_DELETE', message);
      this.$emit('submit-message', message.content, { isTyping: false });
    },
    getTooltipContent(block, message) {
      let content = '';
      const user = block.user;

      if (block.is_typing) {
        content = 'Rozepsaná zpráva';
      } else if (message && message.third_party) {
        return `Volaný, ${block.tooltip_time}`;
      } else if (block.direction === 'in') {
        if (user.is_operator) {
          content = `Operátor ${user.first_name.slice(0, 1)}. ${user.last_name}, ${block.tooltip_time}`;
        } else {
          return `${block.user.name}, ${block.tooltip_time}`;
        }
      } else {
        content = block.tooltip_time;
      }

      return block.tooltip_time || content;
    },
    toggleEditMessage(message) {
      this.$store.dispatch('chat/toggleEditMessage', message);
    },
    editMessage(message) {
      this.$store.dispatch('chat/editMessage', message);
      this.toggleEditMessage(message);
    },
    onScroll: _debounce(function() {
      if (this.$refs.feed) {
        const scrollDelta = Math.abs(this.$refs.feed.scrollTop - (this.$refs.feed.scrollHeight - this.$refs.feed.clientHeight));
        // have some tolerance from the bottom
        this.isScrolledToBottom = scrollDelta < 150;
      }
    }, 50),

    updateIsMessageByThirdParty() {
      if (this.request && this.request.id && window.sessionStorage.getItem(this.isMessageByThirdPartySessionKey)) {
        this.isMessageByThirdParty = true;
      }
    },

    async sendHistoryToEmail() {
      if (this.isHistorySending) {
        return;
      }

      this.isHistorySending = true;

      try {
        await useApi.post(`/client/requests/${this.request.id}/send-chat-history`);
      } catch (error) {
        console.error(error);
        ElMessage.error({
          message: 'Došlo k chybě při odesílání emailu.',
          duration: 5000,
          showClose: true,
        });
      }

      this.isHistorySending = false;
      this.isHistorySent = true;
    },
  },
};
</script>

<style scoped>
@reference "assets/css/tailwind.css";

.Chat {
  display: block;
  position: relative;
  height: 100%;
  overflow: hidden;

  .ScrollToBottomBtnWrapper {
    position: fixed;
    bottom: 0;

    .ScrollToBottomBtn {
      transform: translateX(-50%);
    }
  }

  &.Chat--hasThirdPartMessageSwitch,
  &.Chat--canShowPredefinedMessages {
    .ChatDivWrapper {
      margin-bottom: 82px;
    }
  }

  .ChatDivWrapper {
    position: absolute;
    inset: 0;
    overflow-y: auto;

    padding: 32px 24px;
    padding-bottom: 0;
    margin-bottom: 62px;

    .ChatDiv {
      display: flex;
      flex-direction: column;
    }

    .MessageBlock {
      margin-bottom: 16px;

      .MessageBlockFooter {
        display: flex;

        .BlockLast {
          margin-top: 4px;

          .Timestamp {
            margin-left: 10px;
          }
        }
      }

      .MessageBlockContent {
        display: flex;
        align-items: flex-end;
      }

      .MessageBlockSidePanel {
        display: flex;
        align-items: flex-end;
        height: 100%;
        flex: 0 0 40px;
        margin-right: 16px;

        .UserAvatar {
          position: relative;
          border-radius: 50%;
          height: 40px;
          width: 40px;
          background-size: cover;
          background-position: 50% 50%;
          background-color: #f3f3f3;
          display: flex;
          align-items: center;
          justify-content: center;

          &.online {
            &::after {
              position: absolute;
              bottom: -2px;
              right: -2px;
              content: "";
              height: 12px;
              width: 12px;
              border: 2px solid white;
              border-radius: 50%;
              @apply bg-success;
            }
          }
        }
      }

      &.in {
        padding-right: 56px;

        .MessageBlockFooter {
          .BlockLast {
            margin-left: 56px;
          }
        }

        .MessageBlockSidePanel {
          margin-right: 16px;
        }
      }

      &.out {
        padding-left: 56px;
        align-self: flex-end;
        width: 100%;

        .MessageBlockFooter {
          flex-direction: row-reverse;

          .BlockLast {
            margin-left: 0;
            margin-right: 56px;
          }
        }

        .MessageBlockContent {
          flex-direction: row-reverse;
        }

        .MessageBlockSidePanel {
          margin-right: 0;
          margin-left: 16px;

          .UserAvatar:not(.UserAvatar--ThirdParty) {
            &::before {
              content: "Vy";
              @apply text-muted;
            }
          }
        }
      }
    }
  }

  .InputDiv {
    position: absolute;
    bottom: 0;
    right: 0;
    left: 0;
    min-height: 65px;
    display: flex;
    align-items: center;
    @apply border-t border-border;
    padding-right: 12px;
    background-color: white;
    z-index: 1233;

    .ThirdPartSwitch {
      position: absolute;
      right: 0;
      bottom: 100%;
      @apply bg-white;
      padding: 6px;
      padding-right: 10px;
      opacity: 0.8;
      margin-bottom: 1px;
    }
  }

  &.Slim {
    .ChatDivWrapper .MessageBlock {
      max-width: 95%;

      &.in {
        padding-right: 0;

        .MessageBlockFooter .BlockLast {
          margin-left: 40px;
        }
      }

      .MessageBlockSidePanel {
        width: 30px;
        margin-right: 0;

        .UserAvatar {
          height: 30px;
          width: 30px;
        }
      }

      &.out {
        padding-left: 0;

        .MessageBlockFooter .BlockLast {
          margin-right: 0;
        }
      }
    }
  }
}

.ClosedCard__link {
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  font-size: 0.875rem;
  color: var(--color-text-light);
  cursor: pointer;
  text-decoration: underline;
  text-decoration-color: currentcolor;
  text-decoration-thickness: 1px;
  text-underline-offset: 3px;
  transition: color 0.15s;

  &:hover:not(:disabled) {
    color: var(--color-primary-500);
  }

  &:disabled {
    cursor: wait;
    opacity: 0.5;
  }
}
</style>

<style>
.Chat {
  .InputDiv {
    textarea {
      height: 100%;
      display: block;

      flex: 1 0 auto;
      border: none;
      line-height: 24px;
      font-size: 16px;
      max-height: 200px;
      padding: 5px 15px;

      &:focus {
        outline: none;
      }
    }
  }

  &.BiggerText {
    .InputDiv textarea {
      font-size: 19px;
    }
  }

  &.BiggerText2 {
    .InputDiv textarea {
      font-size: 22px;
    }
  }

  &.BiggerText3 {
    .InputDiv textarea {
      font-size: 30px;
    }
  }
}

.is-ios-app {
  .Chat {
    .InputDiv {
      bottom: env(safe-area-inset-top);
    }
  }
}
</style>
